import { useState, useRef } from 'react';
import {
  Card,
  Stack,
  Text,
  Button,
  Group,
  SimpleGrid,
  ActionIcon,
  Image,
  Center,
  Alert,
  Progress,
  Title,
  Badge,
  Tooltip
} from '@mantine/core';
import {
  IconUpload,
  IconTrash,
  IconPhoto,
  IconX,
  IconPlus,
  IconExclamationCircle,
  IconStar,
  IconStarFilled
} from '@tabler/icons-react';

const ImageUploader = ({ 
  title, 
  description, 
  images = [], 
  onImagesChange, 
  maxImages = 10,
  accept = 'image/*',
  onSetPrimary = null,
  allowPrimarySelection = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const currentCount = images.length;
    const newFiles = Array.from(files);
    const availableSlots = maxImages - currentCount;

    if (newFiles.length > availableSlots) {
      setError(`You can only upload ${availableSlots} more image(s). Maximum is ${maxImages} images.`);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const newImages = [];
      
      for (const file of newFiles) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          setError(`${file.name} is not a valid image file.`);
          continue;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          setError(`${file.name} is too large. Maximum size is 10MB.`);
          continue;
        }

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        
        newImages.push({
          id: Date.now() + Math.random(), // Temporary ID
          file: file,
          preview: previewUrl,
          name: file.name,
          size: file.size,
          uploaded: false
        });
      }

      if (newImages.length > 0) {
        onImagesChange([...images, ...newImages]);
      }
    } catch (err) {
      console.error('Error processing files:', err);
      setError('Error processing selected files.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (imageId) => {
    const updatedImages = images.filter(img => img.id !== imageId);
    
    // Clean up preview URLs to prevent memory leaks
    const imageToRemove = images.find(img => img.id === imageId);
    if (imageToRemove && imageToRemove.preview && imageToRemove.preview.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    
    onImagesChange(updatedImages);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <div>
          <Title order={4}>{title}</Title>
          {description && (
            <Text size="sm" c="dimmed" mt="xs">{description}</Text>
          )}
          <Text size="xs" c="dimmed" mt="xs">
            {images.length}/{maxImages} images uploaded
          </Text>
        </div>

        {error && (
          <Alert 
            icon={<IconExclamationCircle size="1rem" />} 
            color="red" 
            onClose={() => setError(null)}
            withCloseButton
          >
            {error}
          </Alert>
        )}

        {uploading && (
          <Progress value={100} animated color="blue" />
        )}

        {/* Image Grid */}
        <SimpleGrid 
          cols={{ base: 2, sm: 3, md: 4 }} 
          spacing="md"
          style={{ minHeight: images.length === 0 ? 200 : 'auto' }}
        >
          {images.map((image) => (
            <Card key={image.id} shadow="xs" padding="xs" radius="md" withBorder>
              <Stack gap="xs">
                <div style={{ position: 'relative', height: 120 }}>
                  <Image
                    src={image.preview || image.url}
                    alt={image.name || 'Uploaded image'}
                    fit="cover"
                    radius="sm"
                    height={120}
                    fallbackSrc="/api/placeholder/120/120"
                  />
                  {image.is_primary && (
                    <Badge
                      color="yellow"
                      variant="filled"
                      size="xs"
                      style={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        opacity: 0.9
                      }}
                    >
                      Primary
                    </Badge>
                  )}
                  <ActionIcon
                    color="red"
                    size="sm"
                    variant="filled"
                    onClick={() => handleRemoveImage(image.id)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      opacity: 0.9
                    }}
                  >
                    <IconX size="0.8rem" />
                  </ActionIcon>
                  {allowPrimarySelection && onSetPrimary && image.uploaded !== false && (
                    <Tooltip label={image.is_primary ? "Primary image" : "Set as primary"}>
                      <ActionIcon
                        color={image.is_primary ? "yellow" : "gray"}
                        size="sm"
                        variant={image.is_primary ? "filled" : "light"}
                        onClick={() => !image.is_primary && onSetPrimary(image.id)}
                        style={{
                          position: 'absolute',
                          bottom: 4,
                          right: 4,
                          opacity: 0.9
                        }}
                      >
                        {image.is_primary ? <IconStarFilled size="0.8rem" /> : <IconStar size="0.8rem" />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </div>
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {image.name || 'Image'}
                </Text>
              </Stack>
            </Card>
          ))}

          {/* Add New Image Button */}
          {images.length < maxImages && (
            <Card 
              shadow="xs" 
              padding="md" 
              radius="md" 
              withBorder
              style={{ 
                cursor: 'pointer',
                borderStyle: 'dashed',
                height: images.length === 0 ? 200 : 160
              }}
              onClick={triggerFileInput}
            >
              <Center style={{ height: '100%' }}>
                <Stack gap="sm" align="center">
                  <IconPlus size="2rem" color="var(--mantine-color-gray-5)" />
                  <Text size="sm" c="dimmed" ta="center">
                    Add {images.length === 0 ? 'Images' : 'More'}
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    {maxImages - images.length} slots remaining
                  </Text>
                </Stack>
              </Center>
            </Card>
          )}
        </SimpleGrid>

        {/* Upload Actions */}
        <Group gap="md">
          <Button
            leftSection={<IconUpload size="1rem" />}
            onClick={triggerFileInput}
            disabled={images.length >= maxImages || uploading}
            variant="outline"
          >
            Select Images
          </Button>
          
          {images.length > 0 && (
            <Button
              color="red"
              variant="subtle"
              leftSection={<IconTrash size="1rem" />}
              onClick={() => {
                // Clean up all preview URLs
                images.forEach(img => {
                  if (img.preview && img.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(img.preview);
                  }
                });
                onImagesChange([]);
              }}
            >
              Clear All
            </Button>
          )}
        </Group>

        <Text size="xs" c="dimmed">
          Supported formats: JPG, PNG, GIF, WebP. Maximum file size: 10MB per image.
        </Text>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept}
          onChange={(e) => handleFileSelect(e.target.files)}
          style={{ display: 'none' }}
        />
      </Stack>
    </Card>
  );
};

export default ImageUploader;